import { useState } from "react";
import { Loader2, Mail, Phone, Clock, Globe } from "lucide-react";
import { toast } from "sonner";
import { FadeUp, MaskLines, SectionLabel } from "./Reveal";
import { Socials } from "./Socials";
import { api, errMsg, PHONE_DISPLAY, PHONE_TEL, PHONE2_DISPLAY, PHONE2_TEL, EMAIL, DOMAIN } from "@/lib/api";
import { useLang } from "@/lib/i18n";

const inputCls = "w-full border-0 border-b border-white/15 bg-transparent px-0 py-3 text-base text-bone placeholder:text-zinc-500 focus:border-brand focus:outline-none transition-colors";
const EMPTY = { name: "", phone: "", email: "", message: "" };

const ContactForm = () => {
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const { t } = useLang();
  const c = t.contact;
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));
  const submit = async (e) => {
    e.preventDefault();
    if (!f.name.trim() || f.phone.trim().length < 5) return toast.error(t.quote.required);
    setBusy(true);
    try {
      await api.post("/leads", { ...f, kind: "contact", email: f.email || null });
      toast.success(c.ok);
      setF(EMPTY);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <form data-testid="contact-form" onSubmit={submit} className="grid gap-6 sm:grid-cols-2">
      <input data-testid="contact-name-input" className={inputCls} placeholder={c.name} value={f.name} onChange={set("name")} required />
      <input data-testid="contact-phone-input" className={inputCls} placeholder={c.phonePh} type="tel" value={f.phone} onChange={set("phone")} required />
      <input data-testid="contact-email-input" className={`${inputCls} sm:col-span-2`} placeholder={c.emailPh} type="email" value={f.email} onChange={set("email")} />
      <textarea data-testid="contact-message-input" rows={3} className={`${inputCls} resize-none sm:col-span-2`} placeholder={c.message} value={f.message} onChange={set("message")} />
      <button data-testid="contact-submit-button" disabled={busy} className="inline-flex items-center justify-center gap-3 border border-brand px-7 py-4 text-brand transition-colors duration-300 hover:bg-brand hover:text-ink disabled:opacity-60 sm:col-span-2 sm:justify-self-start">
        {busy && <Loader2 className="h-4 w-4 animate-spin" />}
        <span className="font-display text-sm font-extrabold uppercase tracking-wider">{c.send}</span>
      </button>
    </form>
  );
};

const INFO = [
  [Phone, "phone", PHONE_DISPLAY, PHONE_TEL, "contact-phone"],
  [Phone, "phone2", PHONE2_DISPLAY, PHONE2_TEL, "contact-phone-2"],
  [Mail, "email", EMAIL, `mailto:${EMAIL}`, "contact-email"],
  [Clock, "hours", null, null, "contact-hours"],
  [Globe, "web", DOMAIN, null, "contact-web"],
];

export const Contact = () => {
  const { t } = useLang();
  const c = t.contact;
  return (
  <section id="contact" data-testid="contact-section" className="relative bg-ink py-24 md:py-36">
    <div className="mx-auto max-w-[1440px] px-5 md:px-10">
      <SectionLabel n="08">{c.label}</SectionLabel>
      <a data-testid="contact-big-phone" href={PHONE_TEL} className="group mt-8 block">
        <span className="font-display block text-[9vw] font-black leading-[0.95] tracking-[-0.01em] text-bone transition-colors duration-500 group-hover:text-brand sm:text-5xl lg:text-6xl">
          <MaskLines inView lines={[PHONE_DISPLAY]} />
        </span>
      </a>
      <div className="mt-16 grid gap-14 lg:grid-cols-12">
        <FadeUp className="lg:col-span-5">
          <ul className="border-t border-white/10">
            {INFO.map(([Icon, k, v, href, id]) => (
              <li key={k} className="flex items-center gap-5 border-b border-white/10 py-5">
                <Icon className="h-4 w-4 text-brand" />
                <span className="w-20 font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-400">{c[k]}</span>
                {href ? <a data-testid={id} href={href} {...(href.startsWith("http") ? { target: "_blank", rel: "noreferrer" } : {})} className="break-all text-[15px] text-bone transition-colors hover:text-brand sm:text-base">{v}</a> : <span data-testid={id} className="text-bone">{v || c.hoursVal}</span>}
              </li>
            ))}
          </ul>
          <Socials className="mt-6" />
        </FadeUp>
        <FadeUp delay={0.1} className="lg:col-span-7"><ContactForm /></FadeUp>
      </div>
    </div>
  </section>
  );
};
